import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp } from "../context/AppContext";
import MediaPress from "./MediaPress";

const CATS = ["all","exhibition","workshop","competition","talk","festival"];
const CAT_ICONS = { exhibition:"fa-image", workshop:"fa-chalkboard-user", competition:"fa-trophy", talk:"fa-microphone", festival:"fa-star", all:"fa-layer-group" };

const Events = () => {
  const { t, lang } = useApp();
  const isAr = lang === "ar";
  const [view, setView] = useState("media");
  const [events, setEvents] = useState([]);
  const [cat, setCat] = useState("all");
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (view !== "events") return;
    setStatus("loading");
    axios.get(`/api/events?category=${cat}`)
      .then(r => { setEvents(r.data.events || []); setStatus("ready"); })
      .catch(() => setStatus("error"));
  }, [cat, view]);

  const fmt = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString(lang === "ar" ? "ar-SD" : "en-GB", { day:"numeric", month:"long", year:"numeric" });
  };

  const upcoming = events.filter(e => e.status === "upcoming");
  const past     = events.filter(e => e.status === "past");

  const tabStyle = (id) => ({
    display:"flex", alignItems:"center", gap:8, padding:"10px 22px",
    borderRadius:999, border:"2px solid", cursor:"pointer", fontFamily:"inherit",
    fontSize:"0.92rem", fontWeight:700, transition:"all 0.15s",
    color: view === id ? "#fff" : "var(--ink)",
    background: view === id ? "var(--brand)" : "transparent",
    borderColor: view === id ? "var(--brand)" : "var(--line)",
  });

  return (
    <>
      <div style={{ background:"var(--brand)", color:"#fff", padding:"11px 24px", textAlign:"center", fontSize:"0.93rem", fontWeight:500, letterSpacing:"0.01em" }}>
        {isAr
          ? "اكتشف فعاليات ومعارض وورش عمل الفنانين التشكيليين السودانيين — ومتابعة آخر التغطيات الإعلامية والمقابلات"
          : "Discover exhibitions, workshops and events by Sudanese plastic artists — plus media coverage, videos and interviews"}
      </div>
      <div className="page-band">
        <div className="container">
          <h1>{view === "media" ? (isAr ? "الإعلام والصحافة" : "Artists Media & Press") : t("eventsTitle")}</h1>
          <p>{view === "media" ? (isAr ? "مقاطع فيديو، مقابلات، معارض وورش عمل" : "Videos, interviews, exhibitions and workshops") : t("eventsSub")}</p>
        </div>
      </div>

      <section className="section container">
        {/* View toggle tabs */}
        <div style={{ display:"flex", gap:10, marginBottom:32, padding:4, background:"rgba(201,168,76,0.15)", borderRadius:12 }}>
          <button style={tabStyle("media")} onClick={() => setView("media")}>
            <i className="fa-solid fa-film" />
            {isAr ? "الإعلام والصحافة" : "Media & Press"}
          </button>
          <button style={tabStyle("events")} onClick={() => setView("events")}>
            <i className="fa-solid fa-calendar-days" />
            {isAr ? "الفعاليات والأخبار" : "Events & News"}
          </button>
        </div>

        {view === "media" && <MediaPress embedded />}

        {view === "events" && (
          <>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:36 }}>
              {CATS.map(c => (
                <button key={c} className={`cat-btn ${cat===c?"active":""}`}
                  onClick={() => setCat(c)}
                  style={{ display:"flex", alignItems:"center", gap:7, padding:"9px 18px",
                           borderRadius:999, border:"1.5px solid", cursor:"pointer", fontFamily:"inherit",
                           fontSize:"0.9rem", fontWeight:600,
                           color: cat===c ? "#fff" : "var(--ink)",
                           background: cat===c ? "var(--brand)" : "var(--cream)",
                           borderColor: cat===c ? "var(--brand)" : "var(--line)" }}>
                  <i className={`fa-solid ${CAT_ICONS[c]}`} /> {t(c==="all"?"allEvents":c)}
                </button>
              ))}
            </div>

            {status === "loading" && <div className="empty-state">{t("loading")}</div>}
            {status === "error"   && <div className="empty-state">Could not load events.</div>}

            {status === "ready" && (
              <>
                {upcoming.length > 0 && (
                  <div style={{ marginBottom:48 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:22 }}>
                      <span style={{ background:"var(--brand)", color:"#fff", fontSize:"0.75rem", fontWeight:700,
                                     letterSpacing:"0.12em", textTransform:"uppercase", padding:"4px 12px", borderRadius:999 }}>
                        {t("upcoming")}
                      </span>
                      <div style={{ flex:1, height:1, background:"var(--line)" }} />
                    </div>
                    <div style={{ display:"grid", gap:18 }}>
                      {upcoming.map((ev, i) => <EventCard key={i} ev={ev} lang={lang} fmt={fmt} t={t} />)}
                    </div>
                  </div>
                )}

                {past.length > 0 && (
                  <div>
                    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:22 }}>
                      <span style={{ background:"var(--muted)", color:"#fff", fontSize:"0.75rem", fontWeight:700,
                                     letterSpacing:"0.12em", textTransform:"uppercase", padding:"4px 12px", borderRadius:999 }}>
                        {t("past")}
                      </span>
                      <div style={{ flex:1, height:1, background:"var(--line)" }} />
                    </div>
                    <div style={{ display:"grid", gap:18 }}>
                      {past.map((ev, i) => <EventCard key={i} ev={ev} lang={lang} fmt={fmt} t={t} past />)}
                    </div>
                  </div>
                )}

                {events.length === 0 && (
                  <div className="empty-state">No events in this category yet.</div>
                )}
              </>
            )}
          </>
        )}
      </section>
    </>
  );
};

const EventCard = ({ ev, lang, fmt, t, past }) => {
  const [open, setOpen] = useState(false);
  const title = lang === "ar" && ev.titleAr ? ev.titleAr : ev.title;
  const loc   = lang === "ar" && ev.locationAr ? ev.locationAr : ev.location;
  const desc  = lang === "ar" && ev.descriptionAr ? ev.descriptionAr : ev.description;
  const CAT_COLORS = { exhibition:"#9a3324", workshop:"#2e7d60", competition:"#8e5e00", talk:"#1a5276", festival:"#5b2d8e" };
  const color = CAT_COLORS[ev.category] || "var(--brand)";

  return (
    <div className="ev-card" style={{ background:"var(--surface)", border:"1px solid var(--line)",
                                       borderRadius:"var(--radius)", overflow:"hidden",
                                       opacity: past ? 0.8 : 1,
                                       boxShadow:"var(--shadow-sm)", borderLeft:`4px solid ${color}` }}>
      <div style={{ padding:"22px 24px" }}>
        <div className="ev-meta" style={{ display:"flex", alignItems:"center", gap:14, marginBottom:12, flexWrap:"wrap" }}>
          <span style={{ background:color+"22", color, fontSize:"0.75rem", fontWeight:700,
                         letterSpacing:"0.1em", textTransform:"uppercase", padding:"3px 10px", borderRadius:999 }}>
            <i className={`fa-solid ${CAT_ICONS[ev.category] || "fa-calendar"}`} style={{ marginRight:5 }} />
            {t(ev.category)}
          </span>
          <span style={{ color:"var(--muted)", fontSize:"0.88rem" }}>
            <i className="fa-regular fa-calendar" style={{ marginRight:5 }} />
            {fmt(ev.date)}{ev.endDate && ev.endDate !== ev.date ? ` — ${fmt(ev.endDate)}` : ""}
          </span>
          {loc && (
            <span style={{ color:"var(--muted)", fontSize:"0.88rem" }}>
              <i className="fa-solid fa-location-dot" style={{ marginRight:5 }} />{loc}
            </span>
          )}
        </div>
        <h3 style={{ margin:"0 0 8px", fontSize:"1.2rem" }}>{title}</h3>
        {desc && (
          <>
            <p style={{ color:"var(--muted)", margin:0, lineHeight:1.7, fontSize:"0.96rem",
                        display: open ? "block" : "-webkit-box",
                        WebkitLineClamp: open ? "unset" : 2,
                        WebkitBoxOrient:"vertical", overflow: open ? "visible" : "hidden" }}>
              {desc}
            </p>
            {desc.length > 120 && (
              <button onClick={() => setOpen(o=>!o)}
                style={{ background:"none", border:"none", color:"var(--brand)", cursor:"pointer",
                         fontWeight:600, fontSize:"0.85rem", padding:"6px 0 0", fontFamily:"inherit" }}>
                {open ? (lang==="ar" ? "أقل" : "Show less") : (lang==="ar" ? "اقرأ أكثر" : "Read more")}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Events;
